import React from 'react';
import { motion } from 'framer-motion';
import { CalendarCheckIcon, CheckCircle2Icon, HeartIcon, LeafIcon, TrendingUpIcon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const pillars = [{
  icon: LeafIcon,
  title: 'الاستدامة',
  description: 'تموّل المقاعد المدفوعة تقديم برامجنا يومًا بيوم، بما يضمن الجودة والاستمرارية لكل مجموعة.',
  tone: 'sage' as const
}, {
  icon: HeartIcon,
  title: 'الرسالة الاجتماعية',
  description: 'تفتح المنح الدراسية الباب للأسر التي تحتاج إلى الدعم، من خلال معايير شفافة ومقاعد مدعومة من المانحين.',
  tone: 'rose' as const
}, {
  icon: TrendingUpIcon,
  title: 'التوسّع',
  description: 'تتيح لنا المنح المؤسسية والشراكات الوصول إلى مزيد من الأسر في مناطق أكثر مع مرور الوقت.',
  tone: 'sand' as const
}];
const tuitionRows = [{
  age: '5–7',
  format: 'حصتان أسبوعيًا',
  price: '€90'
}, {
  age: '8–10',
  format: 'حصتان أسبوعيًا (مع حصة اختيارية مجانية إضافية)',
  price: '€120'
}, {
  age: '11–14',
  format: '3 حصص أسبوعيًا',
  price: '€150'
}, {
  age: '15–18',
  format: '3 حصص أسبوعيًا',
  price: '€180'
}];
const enrichmentRows = [{
  name: 'لغة إضافية',
  price: '€25–40 / شهريًا'
}, {
  name: 'الخط العربي',
  price: '€20–30 / شهريًا'
}, {
  name: 'الفنون والحِرف الإسلامية',
  price: '€30–50 / شهريًا'
}];
const familyPackages = [{
  icon: UsersIcon,
  eyebrow: 'خصم للأشقاء',
  value: '−15%',
  label: 'تسجيل طفلين',
  note: 'يُطبّق تلقائيًا على الرسوم الدراسية الشهرية.',
  tone: 'sage' as const
}, {
  icon: UsersRoundIcon,
  eyebrow: 'خصم للأشقاء',
  value: '−25%',
  label: 'تسجيل 3 أطفال أو أكثر',
  note: 'أفضل سعر نقدمه للأسر الكبيرة.',
  tone: 'rose' as const
}, {
  icon: CalendarCheckIcon,
  eyebrow: 'التزام سنوي',
  value: 'شهران مجانًا',
  label: 'ادفع مقابل 10 أشهر واحصل على 12',
  note: 'التزم للعام الدراسي ونمنحك شهرين مجانًا.',
  tone: 'sand' as const
}];
const supportTypes = [{
  title: 'منح جزئية',
  description: 'للأسر ذات الدخل المحدود أو التي تمر بصعوبات مالية مؤقتة.',
  tone: 'sage' as const
}, {
  title: 'منح كاملة',
  description: 'دعم ذو أولوية للأطفال الأكثر هشاشة، بما في ذلك حالات الإعاقة والأزمات.',
  tone: 'rose' as const
}, {
  title: 'مقاعد برعاية المجتمع',
  description: 'مقاعد ممولة من المانحين والشركاء، مع معايير وإجراءات إبلاغ واضحة.',
  tone: 'sand' as const
}];
const allocationPoints = ['تتقدم الأسر بطلب يتضمن تقييمًا مختصرًا وسريًا للاحتياج', 'تتبع القرارات معايير منشورة وواضحة', 'تُصان الكرامة؛ ولا تُعرّف أي أسرة علنًا', 'تُرفع تقارير بالنتائج كل ثلاثة أشهر إلى المانحين والشركاء'];
export function ArabicScholarships() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-3099" data-mp="11snsrl9b4r8w">
      <section className="relative overflow-hidden" data-id="element-3100" data-mp="xqj12s2f64ow">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-3101" data-mp="sy9hdtrwe0v2" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-3102" data-mp="nydexyyumhhm" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-3103" data-mp="j1en5n2nqmml">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-3104" data-mp="e0quyy2ozzqs">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-6 lg:col-span-6" data-id="element-3105" data-mp="9znficok45q3">
              <span className="text-[15px] font-semibold text-forest-700" data-id="element-3106" data-mp="52wzfpcr5a4o">إتاحة الفرصة لكل طفل</span>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-3107" data-mp="24bbwsli9gkdn">المنح الدراسية</h1>
              <div className="max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-3108" data-mp="w4zae25tr0p6">
                <p data-id="element-3109" data-mp="111hcsvdommyc">ينبغي أن يكون التعليم متاحًا وكريمًا ومستدامًا.</p>
                <p data-id="element-3110" data-mp="n3j0dvix90lh">تُدعم المنح الدراسية بالتبرعات والشراكات المؤسسية، وتُخصَّص بشفافية استنادًا إلى معايير واضحة.</p>
              </div>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-3111" data-mp="s0j43zsatr9f">
                <Link to="/ar/waitlist" data-id="element-3112" data-mp="dcoc6lm47qxe"><Button variant="primary" size="lg" data-id="element-3113" data-mp="dc52fs6mc8ug">التقدّم بطلب منحة</Button></Link>
                <Link to="/ar/children/kids-programs" data-id="element-3114" data-mp="i46bz6cejlgs"><Button variant="secondary" size="lg" data-id="element-3115" data-mp="i57olf92gg0m">عرض البرامج</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-3116" data-mp="4aoudr3a0ite">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-3117" data-mp="3qm2k29njay4o"><MosqueArchMask src="https://cdn.magicpatterns.com/uploads/8awkEnkAr35x3W2oQBLASf/Scholar-3.jpg" alt="طفلة مسلمة تدرس على حاسوب محمول في مساحة منزلية مضيئة" data-id="element-3118" data-mp="3vkvvzxn6ah2q" /></div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-sage-50/60 py-16 md:py-24" data-id="element-3119" data-mp="3lvgvo3643txd">
        <Container data-id="element-3120" data-mp="3lsuf5e18u430">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3121" data-mp="3gwdpgfeebhnt">نموذجنا الهجين</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-3122" data-mp="3uv3qg64c61hg">نتبع هيكلًا بسيطًا: المقاعد المدفوعة تصنع الاستدامة، والمنح الدراسية تحقق الأثر الاجتماعي، والمنح المؤسسية تتيح التوسّع.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-3123" data-mp="8gk9c1nz887a">
            {pillars.map((pillar, index) => <PillarCard key={pillar.title} pillar={pillar} index={index} data-id="element-3124" data-mp="3go644n3skss" data-mpi={index} />)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3125" data-mp="hhs6ggerb2zs">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3126" data-mp="dgf107fkt1te" />
        <Container data-id="element-3127" data-mp="ddkg6esnhezg">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3128" data-mp="kitv86fgwmli">الرسوم الدراسية</h2>
          <p className="mt-5 max-w-4xl leading-loose text-ink-700" data-id="element-3129" data-mp="piq4ak30v47x">رسوم واضحة ومناسبة للأسر، صُممت بحيث تحافظ المقاعد المدفوعة على استدامة البرنامج وتفتح المنح الدراسية المجال لمن يحتاج إليها.</p>

          <div className="mt-12" data-id="element-3130" data-mp="z8jephrfhdal">
            <h3 className="font-display text-2xl font-semibold leading-loose text-ink-900 md:text-3xl" data-id="element-3131" data-mp="z8sukcire42k">الرسوم الأساسية (شهريًا)</h3>
            <p className="mt-3 leading-loose text-ink-700" data-id="element-3132" data-mp="18867xga07csk"><strong className="text-ink-900" data-id="element-3133" data-mp="186uzh35m5gv4">يشمل:</strong> التعلّم الأساسي / الأساس الإسلامي والثقافي / إثراء أساسي (حصة واحدة)</p>
            <div className="mt-6 overflow-hidden rounded-2xl border border-sage-200 bg-white shadow-soft" data-id="element-3134" data-mp="1d56f5uilall9">
              <div className="grid grid-cols-[0.8fr_1.7fr_auto] gap-3 border-b border-sage-200 bg-sage-50 px-4 py-4 text-xs font-semibold text-ink-900 sm:gap-4 sm:px-6" data-id="element-3135" data-mp="1i3znl8j0rmek"><span data-id="element-3136" data-mp="3jyi72ivzpds3">الفئة العمرية</span><span data-id="element-3137" data-mp="3jxyy3robhzq7">الصيغة</span><span data-id="element-3138" data-mp="3jzjsnhamtio2">السعر</span></div>
              {tuitionRows.map((row, index) => <div key={row.age} className={`grid grid-cols-[0.8fr_1.7fr_auto] items-center gap-3 px-4 py-5 text-sm sm:gap-4 sm:px-6 ${index < tuitionRows.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3139" data-mp="213vu6a1yj2jq" data-mpi={index}><span className="font-display text-lg font-semibold text-ink-900" data-id="element-3140" data-mp="1sb29fx3qrayh">{row.age}</span><span className="leading-loose text-ink-700" data-id="element-3141" data-mp="1nc8xi943rs0f">{row.format}</span><span dir="ltr" className="font-display text-lg font-semibold text-forest-700" data-id="element-3142" data-mp="2k1gi9ewwdx3a">{row.price}</span></div>)}
            </div>
          </div>

          <div className="mt-14" data-id="element-3143" data-mp="2g16ca293v6of">
            <h3 className="text-sm font-semibold text-ink-900" data-id="element-3144" data-mp="2fy0r2y3lkoxz">دروس الإثراء (اختيارية)</h3>
            <div className="mt-5 overflow-hidden rounded-2xl border border-sage-200 bg-white shadow-soft" data-id="element-3145" data-mp="2b2v4ry6a989d">
              <div className="grid grid-cols-[1.5fr_1fr] gap-4 border-b border-sage-200 bg-sage-50 px-6 py-4 text-xs font-semibold text-ink-900" data-id="element-3146" data-mp="2641ojxzm335i"><span data-id="element-3147" data-mp="ulz995j2b60u">الدورة</span><span data-id="element-3148" data-mp="ulh3niaiffg9">السعر</span></div>
              {enrichmentRows.map((row, index) => <div key={row.name} className={`grid grid-cols-[1.5fr_1fr] items-center gap-4 px-6 py-5 ${index < enrichmentRows.length - 1 ? 'border-b border-sage-200' : ''}`} data-id="element-3149" data-mp="2uo8ksxlnbglw" data-mpi={index}><span className="font-medium text-ink-900" data-id="element-3150" data-mp="2zlrxht2p3lbq">{row.name}</span><span dir="ltr" className="font-display text-lg font-semibold text-forest-700" data-id="element-3151" data-mp="2krnfeb5ps3hj">{row.price}</span></div>)}
            </div>
          </div>

          <div className="mt-14" data-id="element-3152" data-mp="2pri4c95xiizy">
            <h3 className="text-sm font-semibold text-ink-900" data-id="element-3153" data-mp="3dezpvmylokuh">باقات الأسرة</h3>
            <p className="mt-3 leading-loose text-ink-700" data-id="element-3154" data-mp="3ie2wvcy55153">هل تسجل أكثر من طفل، أو تلتزم للعام الدراسي؟ نجعل ذلك أسهل بتخفيضات مدمجة.</p>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3155" data-mp="33oero0mu9ic3">{familyPackages.map((item, index) => <PackageCard key={item.label} item={item} index={index} data-id="element-3156" data-mp="38nhr95k1uwco" data-mpi={index} />)}</div>
            <p className="mt-5 flex items-center gap-2 text-sm leading-loose text-ink-500" data-id="element-3157" data-mp="87tuar3ufb8"><CheckCircle2Icon size={16} className="shrink-0 text-forest-700" strokeWidth={1.8} aria-hidden="true" data-id="element-3158" data-mp="4zg0x04ypol8" />يمكن الجمع بين الخصومات والمنح الدراسية عند استيفاء الشروط.</p>
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-[#EDF2F7]/60 py-16 md:py-24" data-id="element-3159" data-mp="1krjw8v3rry5q">
        <PatternDecor side="right" y="bottom" size="md" data-id="element-3160" data-mp="1zfxxx2pg9gkp" />
        <Container data-id="element-3161" data-mp="1zhhekuf0ptn0">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3162" data-mp="1ugtqagi6bqwh">أنواع الدعم</h2>
          <p className="mt-5 leading-loose text-ink-700" data-id="element-3163" data-mp="16q7hgn0ci2lf">لا ينبغي أن يُستبعد أي طفل من برامجنا بسبب الظروف المالية.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-3164" data-mp="11t7mp88y4m9m">{supportTypes.map((item, index) => <SupportCard key={item.title} item={item} index={index} data-id="element-3165" data-mp="1frxj0lnxyhwx" data-mpi={index} />)}</div>
        </Container>
      </section>

      <section className="bg-white/60 py-16 md:py-24" data-id="element-3166" data-mp="1bp0u6d7t5pkj">
        <Container data-id="element-3167" data-mp="2r6u97yvv3d5v">
          <div className="grid items-center gap-12 lg:grid-cols-12" data-id="element-3168" data-mp="2r6b1tc3u8763">
            <div className="lg:col-span-6" data-id="element-3169" data-mp="2r8oyfpe61rfr">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-4xl" data-id="element-3170" data-mp="2n47ffsfmri61">كيف يتم التخصيص</h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3171" data-mp="1oyn9c1sxvn36">{allocationPoints.map(point => <li key={point} className="flex items-start gap-3 leading-loose" data-id="element-3172" data-mp="1kywcbgctkaq7"><CheckCircle2Icon size={18} className="mt-1 shrink-0 text-forest-700" strokeWidth={1.6} aria-hidden="true" data-id="element-3173" data-mp="1g6v2e134iw3c" /><span data-id="element-3174" data-mp="1b8jtptokermp">{point}</span></li>)}</ul>
              <div className="mt-8 flex flex-wrap gap-3" data-id="element-3175" data-mp="1b7iedd0iniz6"><Link to="/ar/waitlist" data-id="element-3176" data-mp="168p2tq4si21n"><Button variant="primary" data-id="element-3177" data-mp="16976h7ts7ijl">التقدّم بطلب</Button></Link><Link to="/ar/partnership/donate-for-children" data-id="element-3178" data-mp="166uioe1cedym"><Button variant="secondary" data-id="element-3179" data-mp="119ks2v27vw97">ادعم طفلًا</Button></Link></div>
            </div>
            <div className="lg:col-span-6" data-id="element-3180" data-mp="x7qz78y9jwjl"><div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-card" data-id="element-3181" data-mp="x922z8rpl4ap"><img src="https://cdn.magicpatterns.com/uploads/xvXUHwEJEt7AMzu9f6ER5q/Book.jpg" alt="طفل مسلم يكتب ملاحظات أثناء الدراسة" className="h-full w-full object-cover" data-id="element-3182" data-mp="s9pharckq39p" /></div></div>
          </div>
        </Container>
      </section>
    </div>;
}
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof pillars)[number];
  index: number;
}) {
  const Icon = pillar.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[pillar.tone]}`} data-id="element-3183" data-mp="2z5swsg5bpjqk"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-3184" data-mp="38v2tyaf0xslj"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3185" data-mp="2ba6ouo54uluh" /></span><h3 className="mt-5 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3186" data-mp="2g7gpz11vwly5">{pillar.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-3187" data-mp="2l4gkqftaa29y">{pillar.description}</p></motion.article>;
}
function PackageCard({
  item,
  index
}: {
  item: (typeof familyPackages)[number];
  index: number;
}) {
  const Icon = item.icon;
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08,
    ease: [0.22, 1, 0.36, 1]
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-3188" data-mp="i589b18upop"><span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-forest-700/10 bg-white text-forest-700" data-id="element-3189" data-mp="1wintovh42yim"><Icon size={22} strokeWidth={1.6} aria-hidden="true" data-id="element-3190" data-mp="25jlyhfzdja29" /></span><p className="mt-5 text-xs font-semibold text-forest-700" data-id="element-3191" data-mp="25k55w2reeg21">{item.eyebrow}</p><p className="mt-2 font-display text-3xl font-semibold leading-none text-ink-900" data-id="element-3192" data-mp="1hv2rr4vz35ca">{item.value}</p><h4 className="mt-4 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3193" data-mp="1dth72xd75mrj">{item.label}</h4><p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-3194" data-mp="1rkw262kdpeey">{item.note}</p></motion.article>;
}
function SupportCard({
  item,
  index
}: {
  item: (typeof supportTypes)[number];
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5,
    delay: index * 0.08
  }} className={`rounded-2xl border p-6 shadow-soft ${toneClasses[item.tone]}`} data-id="element-3195" data-mp="z2rsdl0j1pix"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3196" data-mp="35pz6mwgdqqun">{item.title}</h3><p className="mt-3 text-sm leading-loose text-ink-700" data-id="element-3197" data-mp="3akmz16ei85ei">{item.description}</p></motion.article>;
}